// Sandbox email hooks — send test emails and track sandbox usage

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api'
const SANDBOX_FROM = 'sandbox@onboarding.one-email.com'
const SANDBOX_QUERY_KEY = ['sandbox-send-count'] as const

interface SendSandboxEmailRequest {
  apiKey: string
  to: string
  subject: string
  html?: string
  text?: string
}

interface SendEmailResponse {
  id: string
  status: 'queued'
  created_at: string
}

interface EmailDetail {
  id: string
  status: string
  from: string
  to: string[]
  created_at: string
  sent_at: string | null
  delivered_at: string | null
  events: Array<{
    type: string
    timestamp: string
    transport?: string
  }>
}

/**
 * Sends a sandbox email using the test API key (not session cookies).
 * This ensures dualAuth falls through to API key auth, triggering sandbox mode.
 */
async function sendSandboxEmail(data: SendSandboxEmailRequest): Promise<SendEmailResponse> {
  const response = await fetch(`${BASE_URL}/v1/emails`, {
    method: 'POST',
    // Explicitly omit cookies — we're authenticating via Bearer token,
    // not session. Without this, same-origin requests send the session
    // cookie, dualAuth picks up the session, sets isTestKey=false, and
    // the domain check fails for sandbox@onboarding.one-email.com.
    credentials: 'omit',
    headers: {
      Authorization: `Bearer ${data.apiKey}`,
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      from: SANDBOX_FROM,
      to: [data.to],
      subject: data.subject,
      html: data.html,
      text: data.text,
    }),
  })

  if (!response.ok) {
    let message = response.statusText
    try {
      const body = (await response.json()) as Record<string, unknown>
      const err = (body.error ?? body) as Record<string, unknown>
      message = (err.message as string) ?? message
    } catch {
      // response wasn't JSON
    }
    throw new Error(message)
  }

  return response.json() as Promise<SendEmailResponse>
}

/**
 * Mutation to send a sandbox test email using the test API key.
 */
export function useSendSandboxEmail(): ReturnType<
  typeof useMutation<SendEmailResponse, unknown, SendSandboxEmailRequest>
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: sendSandboxEmail,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SANDBOX_QUERY_KEY })
    },
  })
}

/**
 * Polls for email delivery status after sending.
 * Uses session auth (cookie) for reads — sandbox restriction is only on sends.
 */
export function useSandboxEmailStatus(
  emailId: string | null,
  enabled: boolean,
): ReturnType<typeof useQuery<EmailDetail>> {
  return useQuery({
    queryKey: ['sandbox-email-status', emailId],
    queryFn: () => apiClient.get<EmailDetail>(endpoints.emails.detail(emailId as string)),
    enabled: enabled && !!emailId,
    refetchInterval: 2000,
  })
}

/**
 * Query to check how many sandbox emails have been sent.
 * Used for the "Ready to go live?" nudge banner.
 */
export function useSandboxSendCount(): ReturnType<typeof useQuery<number>> {
  return useQuery({
    queryKey: SANDBOX_QUERY_KEY,
    queryFn: async () => {
      const result = await apiClient.get<{
        data: Array<{ id: string }>
        pagination: { total: number }
      }>(endpoints.emails.list({ per_page: 1, from: SANDBOX_FROM }))
      return result.pagination.total
    },
  })
}

export type { EmailDetail, SendEmailResponse, SendSandboxEmailRequest }
